import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Dialog,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  Select,
  StatusChip,
  Textarea,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { subscribeTicketSignals } from "@/shell/staffSignals";
import { createSupportApi } from "../api";
import { allowedTicketActions, formatLabel, priorityTone, ticketStatusTone } from "../labels";
import type { SupportTicketAction } from "../types";
import { CreateLinkDialog } from "../components/SupportDialogs";
import { createSupportDeskApi, renderMacro, type DeskPerson, type Macro, type TicketView } from "./supportDeskApi";
import styles from "./supportDesk.module.css";

const QUEUES = ["general", "booking", "payment", "creator", "business", "community", "trust_and_safety"];
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function rel(iso: string | null | undefined) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const a = Math.abs(diff) / 1000;
  const v = a < 3600 ? `${Math.max(1, Math.round(a / 60))}m` : a < 86400 ? `${Math.round(a / 3600)}h` : `${Math.round(a / 86400)}d`;
  return diff >= 0 ? `${v} ago` : `in ${v}`;
}
function name(p: DeskPerson | null | undefined, fallback = "Unknown") {
  return p ? (p.display_name ?? (p.handle ? `@${p.handle}` : fallback)) : fallback;
}

const ACTION_LABEL: Partial<Record<SupportTicketAction, string>> = {
  open: "Mark open",
  pending_customer: "Waiting on customer",
  pending_internal: "Waiting on us",
  escalate: "Escalate",
  resolve: "Resolve",
  close: "Close",
  reopen: "Reopen",
};

/**
 * A support ticket as a conversation: the thread in the middle (customer on
 * the left, staff on the right, internal notes as yellow cards in line), a
 * composer that sends to the customer's Play inbox or writes a private note,
 * canned replies, and the customer's context on the side.
 */
export function SupportTicketDeskPage() {
  const { ticketId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createSupportDeskApi(client), [client]);
  const legacy = useMemo(() => createSupportApi(client), [client]);
  const toast = useToast();
  const [view, setView] = useState<TicketView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [macrosOpen, setMacrosOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<{ id: string; ref: string } | null>(null);
  const [closeWith, setCloseWith] = useState<SupportTicketAction | null>(null);
  const [closeReason, setCloseReason] = useState("");
  const threadEnd = useRef<HTMLDivElement | null>(null);
  const lastCount = useRef(0);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Support", to: "/app/support" },
    { label: view ? `#${view.ticket.ref}` : "Ticket" },
  ]);

  const load = useCallback(async (silent = false) => {
    try {
      const v = await api.view(ticketId);
      setView(v);
      setError(null);
    } catch (e) {
      if (!silent) setError(apiErrorMessage(e));
    }
  }, [api, ticketId]);
  useEffect(() => void load(), [load]);
  useLiveRefresh(() => void load(true), { intervalMs: 10_000 });
  // A customer's message appears the moment it is written (i67 signal on
  // `support-ticket:<id>`); the 10 s refresh above is only the fallback.
  useEffect(() => (ticketId ? subscribeTicketSignals(ticketId, () => void load(true)) : undefined), [ticketId, load]);

  const items = useMemo(() => {
    if (!view) return [];
    return [
      ...view.thread.map((m) => ({ kind: "msg" as const, at: m.sent_at, m })),
      ...view.internal_notes.map((n) => ({ kind: "note" as const, at: n.created_at, n })),
    ].sort((a, b) => (a.at < b.at ? -1 : 1));
  }, [view]);
  useEffect(() => {
    if (items.length !== lastCount.current) {
      lastCount.current = items.length;
      threadEnd.current?.scrollIntoView({ block: "end" });
    }
  }, [items.length]);

  async function run(label: string, work: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await work();
      toast.push({ title: label, tone: "success" });
      await load(true);
      return true;
    } catch (e) {
      setActionError(apiErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (!view && !error) return <LoadingState message="Loading ticket…" />;
  if (!view) {
    return <EmptyState title="Ticket unavailable" description={error ?? "Not found or not authorized."} reason="scope"
      action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>} />;
  }
  const t = view.ticket;
  const actions = allowedTicketActions(t.status).filter((a) => a !== "assign");
  const merged = !!t.merged_into;

  return (
    <Stack gap={16}>
      <SectionHeader
        title={`#${t.ref} · ${t.contact_reason ? t.contact_reason.slice(0, 80) : "Support request"}`}
        description={`From ${name(view.requester)} · opened ${fmt(t.created_at)} (${rel(t.created_at)})`}
        actions={<Flex gap={8} wrap>
          <Button variant="secondary" onClick={() => void navigate("/app/support")}>All tickets</Button>
          <Button variant="secondary" onClick={() => setMacrosOpen(true)}>Canned replies</Button>
          <Button variant="secondary" onClick={() => void load()}>Refresh</Button>
        </Flex>}
      />
      <Flex gap={8} wrap align="center">
        <StatusChip tone={ticketStatusTone(t.status)}>{formatLabel(t.status)}</StatusChip>
        <StatusChip tone={priorityTone(t.priority)}>{formatLabel(t.priority)}</StatusChip>
        <StatusChip tone="info">{formatLabel(t.queue)}</StatusChip>
        {t.sla_state === "breached" ? <StatusChip tone="danger">First response overdue</StatusChip>
          : t.sla_state === "due_soon" ? <StatusChip tone="warning">First response due {rel(t.sla_due_at)}</StatusChip>
          : t.sla_state === "met" ? <StatusChip tone="success">Answered in time</StatusChip>
          : t.sla_state === "ok" ? <StatusChip tone="neutral">Reply due {rel(t.sla_due_at)}</StatusChip> : null}
        {t.csat_rating ? <StatusChip tone={t.csat_rating >= 4 ? "success" : t.csat_rating <= 2 ? "danger" : "neutral"}>Rated {t.csat_rating}/5</StatusChip> : null}
        <span style={{ flex: 1 }} />
        {!merged ? (
          <>
            <Select aria-label="Assignee" value={t.owner_user_id ?? ""} disabled={busy} onChange={(e) => {
              const owner = e.target.value;
              if (!owner) return;
              void run("Assigned", () => legacy.applyTicketAction(t.id, "assign", { owner_user_id: owner }));
            }} options={[
              { value: "", label: t.owner_user_id ? name(view.owner) : "Unassigned — assign…" },
              ...view.assignees.filter((a) => a.id !== t.owner_user_id).map((a) => ({ value: a.id, label: `Assign to ${name(a)}` })),
            ]} />
            {actions.map((a) => (
              <Button key={a} size="sm" variant={a === "resolve" ? "primary" : "secondary"} disabled={busy}
                onClick={() => (a === "close" || a === "escalate" ? (setCloseWith(a), setCloseReason("")) : void run(ACTION_LABEL[a] ?? formatLabel(a), () => legacy.applyTicketAction(t.id, a, {})))}>
                {ACTION_LABEL[a] ?? formatLabel(a)}
              </Button>
            ))}
          </>
        ) : null}
      </Flex>
      {merged ? <Alert tone="info" title="Merged">This ticket was merged into <Link to={`/app/support/tickets/${t.merged_into?.id}`}>another ticket</Link>. Its messages moved there.</Alert> : null}
      {actionError ? <Alert tone="danger" title="Not applied">{actionError}</Alert> : null}

      <div className={styles.layout}>
        <Stack gap={12}>
          <div className={styles.thread}>
            {t.contact_reason ? (
              <div className={`${styles.msg} ${styles.customer}`}>
                <Text role="caption" color="muted" as="div">{name(view.requester)} · opened the request · {fmt(t.created_at)}</Text>
                <div className={styles.body}>{t.contact_reason}</div>
              </div>
            ) : null}
            {items.length === 0 && !t.contact_reason ? <Text role="small" color="muted" as="p">No messages yet.</Text> : null}
            {items.map((it) =>
              it.kind === "note" ? (
                <div key={`n-${it.n.id}`} className={`${styles.msg} ${styles.noteMsg}`}>
                  <Text role="caption" color="secondary" as="div">Internal note · {name(it.n.author)} · {fmt(it.n.created_at)} · not visible to the customer</Text>
                  <div className={styles.body}>{it.n.body}</div>
                </div>
              ) : it.m.message_type === "system_message" || it.m.message_type === "internal_reference" ? (
                <div key={it.m.id} className={`${styles.msg} ${styles.system}`}>{it.m.body_reference} · {fmt(it.m.sent_at)}</div>
              ) : (
                <div key={it.m.id} className={`${styles.msg} ${it.m.from_customer ? styles.customer : styles.agent}`}>
                  <Text role="caption" color="muted" as="div">
                    {it.m.from_customer ? name(it.m.author, "Customer") : `${name(it.m.author, "HUDDL Support")} · staff`} · {fmt(it.m.sent_at)}
                  </Text>
                  <div className={styles.body}>{it.m.body_reference}</div>
                </div>
              ),
            )}
            <div ref={threadEnd} />
          </div>
          {!merged ? <Composer view={view} api={api} legacyCreateNote={(body) => legacy.createNote(t.id, { body_reference: body, classification: "internal" })} onSent={() => load(true)} /> : null}
          {t.csat_comment ? (
            <div className={styles.panel}><Text role="label" as="div">Customer feedback</Text><Text role="small" as="p">“{t.csat_comment}”</Text></div>
          ) : null}
        </Stack>

        <div className={styles.side}>
          <div className={styles.panel}>
            <Text role="label" as="div">Customer</Text>
            {view.requester ? (
              <Link to={`/app/users/${view.requester.id}`}><Text role="bodyStrong" as="span">{name(view.requester)}</Text></Link>
            ) : <Text role="small" as="span">Unknown</Text>}
            {view.requester?.handle ? <Text role="caption" color="muted" as="div">@{view.requester.handle}</Text> : null}
            {view.requester?.email ? <Text role="small" as="div">{view.requester.email}</Text> : null}
            <div className={styles.row}><span>Member since</span><span>{fmt(view.customer.joined_at)}</span></div>
            {view.customer.location ? <div className={styles.row}><span>Location</span><span>{view.customer.location}</span></div> : null}
            <div className={styles.row}><span>Account</span><span>{view.requester?.account_status ?? "—"}{view.customer.restriction ? ` · ${view.customer.restriction}` : ""}</span></div>
            {view.customer.reports_against > 0 ? (
              <div className={styles.row}><span>Reports against</span><Link to="/app/trust-safety?tab=queue">{view.customer.reports_against}</Link></div>
            ) : null}
          </div>

          <div className={styles.panel}>
            <Text role="label" as="div">Recent bookings</Text>
            {view.customer.recent_bookings.length === 0 ? <Text role="small" color="muted" as="p">None.</Text> : view.customer.recent_bookings.map((b) => (
              <div key={b.id} className={styles.row}>
                <Link to={`/app/bookings/${b.id}`}>{b.public_id}</Link>
                <span>{b.business_name ?? ""} · {formatLabel(b.status)} · {fmt(b.starts_at)}</span>
              </div>
            ))}
          </div>

          <div className={styles.panel}>
            <Text role="label" as="div">Other requests from this customer</Text>
            {view.customer.other_tickets.length === 0 ? <Text role="small" color="muted" as="p">This is their first.</Text> : view.customer.other_tickets.map((o) => (
              <div key={o.id} className={styles.row}>
                <Link to={`/app/support/tickets/${o.id}`}>#{o.ref}</Link>
                <span>{formatLabel(o.status)}{o.csat_rating ? ` · ${o.csat_rating}/5` : ""}</span>
                {!merged && !["closed"].includes(o.status) ? <Button size="sm" variant="ghost" onClick={() => setMergeTarget({ id: o.id, ref: o.ref })}>Merge here</Button> : null}
              </div>
            ))}
          </div>

          <DetailsPanel view={view} api={api} busy={busy} run={run} />

          <div className={styles.panel}>
            <Flex justify="space-between" align="center"><Text role="label" as="div">Linked records</Text><Button size="sm" variant="ghost" onClick={() => setLinkOpen(true)}>Attach</Button></Flex>
            {view.links.length === 0 ? <Text role="small" color="muted" as="p">None.</Text> : view.links.map((l) => (
              <div key={l.id} className={styles.row}><span>{formatLabel(l.entity_type)}</span><span>{l.label ?? `${l.entity_id.slice(0, 8)}…`} · {formatLabel(l.link_type)}</span></div>
            ))}
          </div>
        </div>
      </div>

      <CreateLinkDialog open={linkOpen} busy={busy} error={actionError} onClose={() => setLinkOpen(false)}
        onSubmit={async (input) => { if (await run("Link attached", () => legacy.createLink(t.id, input))) setLinkOpen(false); }} />
      <MacrosDialog open={macrosOpen} api={api} onClose={() => setMacrosOpen(false)} />
      <Dialog open={!!mergeTarget} onClose={() => setMergeTarget(null)} title={`Merge #${mergeTarget?.ref ?? ""} into this ticket?`} actions={
        <Flex gap={8}><Button variant="secondary" onClick={() => setMergeTarget(null)}>Cancel</Button>
          <Button loading={busy} onClick={() => { const m = mergeTarget; setMergeTarget(null); if (m) void run("Merged", () => api.merge(m.id, t.id)); }}>Merge</Button></Flex>
      }>
        <Text role="small" as="p">Its messages move here and it is closed as a duplicate. It is not deleted.</Text>
      </Dialog>
      <Dialog open={!!closeWith} onClose={() => setCloseWith(null)} title={closeWith === "close" ? "Close this ticket?" : "Escalate this ticket?"} actions={
        <Flex gap={8}><Button variant="secondary" onClick={() => setCloseWith(null)}>Cancel</Button>
          <Button loading={busy} disabled={closeReason.trim().length > 0 && closeReason.trim().length < 3} onClick={() => {
            const a = closeWith; setCloseWith(null);
            if (a) void run(ACTION_LABEL[a] ?? a, () => legacy.applyTicketAction(t.id, a, closeReason.trim() ? { reason: closeReason.trim() } : {}));
          }}>{closeWith === "close" ? "Close" : "Escalate"}</Button></Flex>
      }>
        <Textarea label="Reason (optional, kept in the audit trail)" value={closeReason} onChange={(e) => setCloseReason(e.target.value)} rows={3} />
      </Dialog>
    </Stack>
  );
}

function Composer({ view, api, legacyCreateNote, onSent }: {
  view: TicketView;
  api: ReturnType<typeof createSupportDeskApi>;
  legacyCreateNote: (body: string) => Promise<unknown>;
  onSent: () => Promise<void>;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [text, setText] = useState("");
  const [macroId, setMacroId] = useState<string | undefined>(undefined);
  const [macros, setMacros] = useState<Macro[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const t = view.ticket;

  const openPicker = async () => {
    setPickerOpen((v) => !v);
    if (!macros) {
      try { setMacros((await api.macros()).macros); } catch (e) { setErr(apiErrorMessage(e)); }
    }
  };
  const insert = (m: Macro) => {
    setText((cur) => (cur.trim() ? `${cur}\n\n` : "") + renderMacro(m.body, { name: view.requester?.display_name ?? null, ticketRef: t.ref }));
    setMacroId(m.id);
    setPickerOpen(false);
  };
  const send = async (then: "keep" | "pending_customer" | "resolve") => {
    setBusy(true); setErr(null);
    try {
      if (mode === "note") {
        await legacyCreateNote(text.trim());
        toast.push({ title: "Internal note added", tone: "success" });
      } else {
        await api.reply(t.id, { body: text.trim(), then, ...(macroId ? { macro_id: macroId } : {}) });
        toast.push({ title: then === "resolve" ? "Sent and resolved" : "Sent to the customer's HUDDL inbox", tone: "success" });
      }
      setText(""); setMacroId(undefined);
      await onSent();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const saveAsMacro = async () => {
    const title = window.prompt("Name this canned reply");
    if (!title?.trim()) return;
    try {
      await api.createMacro({ title: title.trim(), body: text.trim(), queue: t.queue });
      setMacros(null);
      toast.push({ title: "Saved as a canned reply", tone: "success" });
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  const shown = (macros ?? []).filter((m) => !filter || `${m.title} ${m.body}`.toLowerCase().includes(filter.toLowerCase()));
  const done = ["resolved", "closed"].includes(t.status);

  return (
    <div className={`${styles.composer} ${mode === "note" ? styles.composerNote : ""}`}>
      <Flex justify="space-between" align="center">
        <div className={styles.modeTabs}>
          <button type="button" className={`${styles.modeTab} ${mode === "reply" ? styles.modeTabOn : ""}`} onClick={() => setMode("reply")}>Reply to customer</button>
          <button type="button" className={`${styles.modeTab} ${mode === "note" ? styles.modeTabOn : ""}`} onClick={() => setMode("note")}>Internal note</button>
        </div>
        {mode === "reply" ? <Button size="sm" variant="ghost" onClick={() => void openPicker()}>Canned replies</Button> : null}
      </Flex>
      {pickerOpen ? (
        <Stack gap={8}>
          <Input placeholder="Search canned replies" value={filter} onChange={(e) => setFilter(e.target.value)} />
          {!macros ? <Text role="small" color="muted" as="p">Loading…</Text>
            : shown.length === 0 ? <Text role="small" color="muted" as="p">{macros.length === 0 ? "No canned replies yet. Write a reply and use “Save as canned reply”, or add them from the Canned replies button above." : "No match."}</Text>
            : (
              <div className={styles.macroList}>
                {shown.map((m) => (
                  <button key={m.id} type="button" className={styles.macroItem} onClick={() => insert(m)}>
                    <Text role="bodyStrong" as="div">{m.title}</Text>
                    <Text role="caption" color="muted" as="div">{m.body.slice(0, 120)}</Text>
                  </button>
                ))}
              </div>
            )}
        </Stack>
      ) : null}
      <Textarea
        aria-label={mode === "note" ? "Internal note" : "Reply"}
        placeholder={mode === "note" ? "Only staff can see this." : done ? "Replying reopens this request." : `Reply to ${view.requester?.display_name ?? "the customer"}…`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        maxLength={5000}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && text.trim() && !busy) void send("keep"); }}
      />
      {err ? <Alert tone="danger">{err}</Alert> : null}
      <Flex gap={8} wrap justify="space-between" align="center">
        <Text role="caption" color="muted" as="span">
          {mode === "reply" ? "Delivered to their HUDDL inbox and Support screen. Ctrl+Enter sends." : "Private. Never sent to the customer."}
        </Text>
        <Flex gap={8} wrap>
          {mode === "reply" && text.trim() ? <Button size="sm" variant="ghost" onClick={() => void saveAsMacro()}>Save as canned reply</Button> : null}
          {mode === "reply" ? (
            <>
              <Button variant="secondary" loading={busy} disabled={!text.trim()} onClick={() => void send("pending_customer")}>Send, wait for customer</Button>
              <Button variant="secondary" loading={busy} disabled={!text.trim()} onClick={() => void send("resolve")}>Send and resolve</Button>
              <Button loading={busy} disabled={!text.trim()} onClick={() => void send("keep")}>Send</Button>
            </>
          ) : (
            <Button loading={busy} disabled={!text.trim()} onClick={() => void send("keep")}>Add note</Button>
          )}
        </Flex>
      </Flex>
    </div>
  );
}

function DetailsPanel({ view, api, busy, run }: {
  view: TicketView;
  api: ReturnType<typeof createSupportDeskApi>;
  busy: boolean;
  run: (label: string, work: () => Promise<unknown>) => Promise<boolean>;
}) {
  const t = view.ticket;
  const [tag, setTag] = useState("");
  const addTag = () => {
    const v = tag.trim().toLowerCase();
    if (!v || t.tags.includes(v)) return;
    setTag("");
    void run("Tag added", () => api.updateFields(t.id, { tags: [...t.tags, v] }));
  };
  return (
    <div className={styles.panel}>
      <Text role="label" as="div">Details</Text>
      <Select label="Priority" value={t.priority} disabled={busy} onChange={(e) => void run("Priority updated", () => api.updateFields(t.id, { priority: e.target.value }))}
        options={PRIORITIES.map((p) => ({ value: p, label: formatLabel(p) }))} />
      <Select label="Category" value={t.queue} disabled={busy} onChange={(e) => void run("Category updated", () => api.updateFields(t.id, { queue: e.target.value }))}
        options={QUEUES.map((q) => ({ value: q, label: formatLabel(q) }))} />
      <Text role="caption" color="secondary" as="div">Tags</Text>
      <Flex gap={4} wrap>
        {t.tags.length === 0 ? <Text role="caption" color="muted" as="span">None</Text> : t.tags.map((x) => (
          <span key={x} className={styles.tag}>{x}
            <button type="button" aria-label={`Remove ${x}`} className={styles.tagX} onClick={() => void run("Tag removed", () => api.updateFields(t.id, { tags: t.tags.filter((y) => y !== x) }))}>×</button>
          </span>
        ))}
      </Flex>
      <Flex gap={4}>
        <Input aria-label="New tag" placeholder="add tag" value={tag} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTag(); }} />
        <Button size="sm" variant="secondary" disabled={!tag.trim()} onClick={addTag}>Add</Button>
      </Flex>
      <div className={styles.row}><span>Reply due</span><span>{fmt(t.sla_due_at)}</span></div>
      <div className={styles.row}><span>First response</span><span>{t.first_response_at ? fmt(t.first_response_at) : "Not yet"}</span></div>
      <div className={styles.row}><span>Resolved</span><span>{fmt(t.resolved_at)}</span></div>
      <div className={styles.row}><span>Satisfaction</span><span className={styles.stars}>{t.csat_rating ? "★".repeat(t.csat_rating) + "☆".repeat(5 - t.csat_rating) : <Text role="caption" color="muted" as="span">{["resolved", "closed"].includes(t.status) ? "Not rated yet" : "Asked after resolve"}</Text>}</span></div>
      {t.merged_from.length > 0 ? <div className={styles.row}><span>Merged in</span><span>{t.merged_from.map((m) => `#${m.public_id.replace(/^tkt_/, "").slice(0, 8).toUpperCase()}`).join(", ")}</span></div> : null}
    </div>
  );
}

function MacrosDialog({ open, api, onClose }: { open: boolean; api: ReturnType<typeof createSupportDeskApi>; onClose: () => void }) {
  const toast = useToast();
  const [macros, setMacros] = useState<Macro[] | null>(null);
  const [editing, setEditing] = useState<Macro | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [queue, setQueue] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setMacros((await api.macros(true)).macros); } catch (e) { setErr(apiErrorMessage(e)); }
  }, [api]);
  useEffect(() => { if (open) { setEditing(null); void load(); } }, [open, load]);
  const startEdit = (m: Macro | "new") => {
    setEditing(m);
    setTitle(m === "new" ? "" : m.title);
    setBody(m === "new" ? "" : m.body);
    setQueue(m === "new" ? "" : (m.queue ?? ""));
    setErr(null);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (editing === "new") await api.createMacro({ title: title.trim(), body: body.trim(), queue: queue || null });
      else if (editing) await api.updateMacro(editing.id, { title: title.trim(), body: body.trim(), queue: queue || null });
      toast.push({ title: "Canned reply saved", tone: "success" });
      setEditing(null);
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} title="Canned replies" actions={<Button variant="secondary" onClick={onClose}>Done</Button>}>
      <Stack gap={12}>
        {editing ? (
          <>
            <Input label="Name" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Select label="Category (optional)" value={queue} onChange={(e) => setQueue(e.target.value)} options={[{ value: "", label: "Any" }, ...QUEUES.map((q) => ({ value: q, label: formatLabel(q) }))]} />
            <Textarea label="Text" hint="{{name}} becomes the customer's first name, {{ticket}} the request number." value={body} onChange={(e) => setBody(e.target.value)} rows={6} />
            {err ? <Alert tone="danger">{err}</Alert> : null}
            <Flex gap={8}><Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button><Button loading={busy} disabled={!title.trim() || !body.trim()} onClick={() => void save()}>Save</Button></Flex>
          </>
        ) : (
          <>
            <Button onClick={() => startEdit("new")}>New canned reply</Button>
            {err ? <Alert tone="danger">{err}</Alert> : null}
            {!macros ? <LoadingState message="Loading…" /> : macros.length === 0 ? (
              <Text role="small" color="muted" as="p">None yet. Write the answers your team gives most often.</Text>
            ) : macros.map((m) => (
              <Flex key={m.id} justify="space-between" align="center" gap={8}>
                <Stack gap={4}>
                  <Text role="bodyStrong" as="span">{m.title}{m.is_active ? "" : " (archived)"}</Text>
                  <Text role="caption" color="muted" as="span">Used {m.use_count} times{m.queue ? ` · ${formatLabel(m.queue)}` : ""}</Text>
                </Stack>
                <Flex gap={4}>
                  <Button size="sm" variant="secondary" onClick={() => startEdit(m)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => void api.updateMacro(m.id, { is_active: !m.is_active }).then(load).catch((e) => setErr(apiErrorMessage(e)))}>{m.is_active ? "Archive" : "Restore"}</Button>
                </Flex>
              </Flex>
            ))}
          </>
        )}
      </Stack>
    </Dialog>
  );
}
